import React, { useState, useRef } from "react";
import { Mail, User, Send, Star, MessageSquare } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const Contact = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const turnstileRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    rating: 0,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!captchaToken) {
      toast.error("Please complete the CAPTCHA verification.");
      setIsSubmitting(false);
      return;
    }
    const payload = { ...formData, captcha: captchaToken };

    try {
      const response = await fetch("https://orchestration.trackdsa.in/user/contact", {
        method: "POST",
        body: JSON.stringify(payload),
        headers: { "Content-type": "application/json; charset=UTF-8" },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success("Message sent! Thank you for contacting.");
        navigate("/");
      } else {
        toast.error("Failed to send message. Please try again later.");
        
      }
    } catch (err) {
      throw new Error("Submission failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
        <h2 className="text-2xl font-bold text-slate-800 mb-2">Get in Touch</h2>
        <p className="text-slate-500 mb-8">We value your feedback. Let us know what you think!</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                placeholder="John Doe"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                placeholder="john@example.com"
              />
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Feedback</label>
            <div className="relative">
              <MessageSquare className="absolute left-3 top-4 w-4 h-4 text-slate-400" />
              <textarea
                rows={4}
                required
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all resize-none"
                placeholder="How can we improve?"
              />
            </div>
          </div>

          {/* Rating */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Rating</label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setFormData({ ...formData, rating: star })}
                  className={`p-1 transition-transform hover:scale-110 ${star <= formData.rating ? 'text-yellow-400' : 'text-slate-300'}`}
                >
                  <Star className="w-8 h-8 fill-current" />
                </button>
              ))}
            </div>
          </div>

          {/* Recaptcha */}
          <div className="flex justify-center py-2">
            <Turnstile
              ref={turnstileRef}
              siteKey="0x4AAAAAAFUeq6pbvQmVIIZ1"
              onSuccess={(token) => {
                setCaptchaToken(token);
                setCaptchaVerified(true);
              }}
              onExpire={() => {
                setCaptchaToken("");
                setCaptchaVerified(false);
              }}
              onError={() => {
                setCaptchaToken("");
                setCaptchaVerified(false);
              }}
            />
          </div>
          {/* Submit */}
          <button
            type="submit"
            disabled={!captchaVerified || isSubmitting}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-semibold py-3 rounded-xl transition-all shadow-sm"
          >
            {isSubmitting ? "Sending..." : "Send Message"}
            {!isSubmitting && <Send className="w-4 h-4" />}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Contact;